
import React, { useState, useEffect } from 'react';
import './LoginPage.css';

interface LoginPageProps {
  onLogin: () => void;
}

type LoginState = 'idle' | 'loading' | 'fingerprint' | 'success' | 'error';

export default function LoginPage({ onLogin }: LoginPageProps) {
  const [loginState, setLoginState] = useState<LoginState>('idle');
  const [progress, setProgress] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Progress animation during loading
  useEffect(() => {
    if (loginState === 'loading') {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setLoginState('fingerprint');
            return 100;
          }
          return prev + 2;
        });
      }, 40);
      return () => clearInterval(interval);
    }
  }, [loginState]);

  // Fingerprint animation completion
  useEffect(() => {
    if (loginState === 'fingerprint') {
      const timer = setTimeout(() => {
        setLoginState('success');
        setTimeout(onLogin, 800);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loginState, onLogin]);

  const handleSignIn = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setProgress(0);
    setLoginState('loading');
  };

  const handleBiometricLogin = () => {
    setErrorMessage('');
    setProgress(0);
    setLoginState('loading');
  };

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="login-page">
      {/* Animated Background Particles */}
      <div className="login-particles">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="login-particle"
            style={{
              width: Math.random() * 100 + 20 + 'px',
              height: Math.random() * 100 + 20 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      <div className="login-container">
        {/* AMP Logo */}
        <div className="login-logo-section">
          <div className="login-logo">
            <div className="login-logo-icon">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Outer Ring */}
                <circle cx="50" cy="50" r="45" stroke="url(#logoGradient)" strokeWidth="3" fill="none" opacity="0.3"/>
                {/* Inner Ring with Glow */}
                <circle cx="50" cy="50" r="35" stroke="url(#logoGradient)" strokeWidth="2" fill="none">
                  <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite"/>
                </circle>
                {/* AMP Text */}
                <text x="50" y="58" textAnchor="middle" fill="url(#logoGradient)" fontSize="24" fontWeight="bold" fontFamily="Inter, sans-serif">
                  AMP
                </text>
                {/* Star Accent */}
                <path d="M50 15 L52 22 L59 22 L54 27 L56 34 L50 30 L44 34 L46 27 L41 22 L48 22 Z" fill="url(#logoGradient)">
                  <animate attributeName="opacity" values="0.6;1;0.6" dur="1.5s" repeatCount="indefinite"/>
                </path>
                <defs>
                  <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#22d3ee"/>
                    <stop offset="50%" stopColor="#67e8f9"/>
                    <stop offset="100%" stopColor="#38bdf8"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="login-logo-dot" />
          </div>
          <p className="login-tagline">ALLSTAR MEDIA PRO</p>
        </div>

        {/* Login Form - Idle State */}
        {loginState === 'idle' && (
          <form className="login-form" onSubmit={handleSignIn}>
            {/* Email Field */}
            <div className="login-field">
              <div className="login-field-glow" />
              <div className="login-field-inner">
                <svg className="login-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-input"
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="login-field">
              <div className="login-field-glow" />
              <div className="login-field-inner">
                <svg className="login-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="login-password-toggle"
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Reset Password Link */}
            <div className="login-reset">
              <button type="button" className="login-reset-btn">
                reset password
              </button>
            </div>

            {/* Sign In Button */}
            <button type="submit" className="login-submit">
              SIGN IN
            </button>

            {/* Biometric Login */}
            <button type="button" className="login-biometric" onClick={handleBiometricLogin}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 10v4"/>
                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
                <path d="M12 6a6 6 0 0 0-6 6"/>
                <path d="M18 12a6 6 0 0 0-6-6"/>
                <path d="M12 18a6 6 0 0 0 6-6"/>
                <path d="M6 12a6 6 0 0 0 6 6"/>
                <path d="M12 8a4 4 0 0 0-4 4"/>
                <path d="M16 12a4 4 0 0 0-4-4"/>
              </svg>
              <span>Use Biometrics</span>
            </button>

            {/* Sign Up Link */}
            <p className="login-signup">
              Don't have an account?{' '}
              <button type="button" className="login-signup-btn">
                Sign Up
              </button>
            </p>
          </form>
        )}

        {/* Loading State with Circular Progress */}
        {loginState === 'loading' && (
          <div className="login-loading">
            <div className="login-progress-ring">
              <svg viewBox="0 0 100 100">
                {/* Background Circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="rgba(6, 182, 212, 0.2)"
                  strokeWidth="4"
                />
                {/* Progress Circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="url(#progressGradient)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  style={{
                    strokeDasharray: circumference,
                    strokeDashoffset: strokeDashoffset,
                    transform: 'rotate(-90deg)',
                    transformOrigin: '50% 50%',
                    transition: 'stroke-dashoffset 0.1s ease-out',
                  }}
                />
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#22d3ee"/>
                    <stop offset="100%" stopColor="#38bdf8"/>
                  </linearGradient>
                </defs>
              </svg>
              <span className="login-progress-text">{Math.round(progress)}%</span>
            </div>
            <p className="login-loading-text">Authenticating...</p>
          </div>
        )}

        {/* Fingerprint Verification */}
        {loginState === 'fingerprint' && (
          <div className="login-fingerprint">
            <div className="login-fingerprint-circle">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 10v4"/>
                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
                <path d="M12 6a6 6 0 0 0-6 6"/>
                <path d="M18 12a6 6 0 0 0-6-6"/>
                <path d="M12 18a6 6 0 0 0 6-6"/>
                <path d="M6 12a6 6 0 0 0 6 6"/>
                <path d="M12 8a4 4 0 0 0-4 4"/>
                <path d="M16 12a4 4 0 0 0-4-4"/>
              </svg>
            </div>
            <p className="login-fingerprint-text">Verifying Identity...</p>
          </div>
        )}

        {/* Success State */}
        {loginState === 'success' && (
          <div className="login-success">
            <div className="login-success-circle">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <p className="login-success-text">Welcome Back!</p>
          </div>
        )}

        {/* Error State */}
        {loginState === 'error' && (
          <div className="login-error">
            <div className="login-error-circle">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <p className="login-error-text">{errorMessage || 'Authentication Failed'}</p>
            <button className="login-retry-btn" onClick={() => setLoginState('idle')}>
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
